import { FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  PageLayout,
  EmptyContainer,
  EMPTY_ILLUSTRATIONS,
  Typography,
  TYPOGRAPHY_SIZE,
  Button,
  ICONS,
  PLATFORM_THEME_COLORS,
  Table,
  CheckboxInput,
  Loader,
} from '@thinknet-gmbh/platform-common-ui';

import { useGetNotifications } from 'src/hooks/notifications/get-notifications.hook';
import { dateFormat } from 'src/common/helpers/date.helpers';
import { useReadAllNotifications } from 'src/hooks/notifications/read-all-notifications.hook';
import { useReadNotification } from 'src/hooks/notifications/read-notification.hook';

import { HeaderContainer, NotificationContainer, CheckboxContainer } from './notifications.styles';

const Notifications: FC = () => {
  const { t } = useTranslation('notifications');

  const { notifications, loading, paginationConfig } = useGetNotifications({
    pageSize: 12,
    skipPopupNotifications: true,
  });
  const readAllNotifications = useReadAllNotifications();
  const readNotification = useReadNotification();

  const markAllAsRead = () => {
    readAllNotifications();
  };

  const onClickMarkAsRead = (notificationId: string) => {
    readNotification({
      variables: {
        notificationId,
      },
    });
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <PageLayout pageTitle={t('titles.notifications')}>
      <NotificationContainer>
        <HeaderContainer>
          <Typography weight="bold" size={TYPOGRAPHY_SIZE.medium}>
            {t('titles.notifications')}
          </Typography>
          <Button
            onClick={markAllAsRead}
            type="primary"
            icon={ICONS.checkDouble}
            iconColor={PLATFORM_THEME_COLORS.primary.white}
          >
            {t('buttons.markAllAsRead')}
          </Button>
        </HeaderContainer>
        {notifications.length > 0 ? (
          <Table
            t={t}
            dataSource={notifications}
            pagination={paginationConfig}
            rowKey={(record: any) => record._id}
            onRow={(record: any) => ({
              onClick: () => {
                if (!record.isReadByAccount) onClickMarkAsRead(record._id);
              },
            })}
            rowClassName={(record: any) => {
              return record.isReadByAccount ? 'checked' : '';
            }}
          >
            <Table.Column
              title={t('titles.needsAttention')}
              render={record => {
                const variablesKeys = Object.keys(record.translationVariables);
                const values = variablesKeys.reduce((result, key) => {
                  return Object.assign(result, {
                    [key]: record.translationVariables[key]
                      ? record.translationVariables[key]
                      : t('notifications:titles.deleted'),
                  });
                }, {});

                return (
                  <div>
                    <span>
                      <Trans
                        i18nKey={record.translationPath}
                        values={values}
                        components={{
                          target: <span style={{ color: PLATFORM_THEME_COLORS.primary.blue }} />,
                        }}
                      />
                    </span>
                  </div>
                );
              }}
            />
            <Table.Column
              title={t('titles.creationsDate')}
              width={140}
              dataIndex="createdAt"
              key="createdAt"
              render={createdAt => <div>{dateFormat(createdAt)}</div>}
            />
            <Table.Column
              title={t('titles.markAsRead')}
              width={120}
              render={(record: any) => {
                return (
                  <CheckboxContainer>
                    <CheckboxInput
                      id={record._id}
                      checked={record.isReadByAccount}
                      onChange={e => {
                        if (e.target.checked) onClickMarkAsRead(record._id);
                      }}
                    />
                  </CheckboxContainer>
                );
              }}
            />
          </Table>
        ) : (
          <EmptyContainer
            emptyTitle={t('emptyData.emptyTitle')}
            illustration={EMPTY_ILLUSTRATIONS.newsletter}
            contentText={t('emptyData.contentText')}
          />
        )}
      </NotificationContainer>
    </PageLayout>
  );
};

export default Notifications;
